import React, { useState } from "react";
import InputMask from "react-input-mask";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import { clearNumber, formatCpf, validadeCpf } from "../../../services/functions";
import { searchCustomerByCpf } from "../../../services/support";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 520,
    margin: "0 auto",
    padding: theme.spacing(3, 0),
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  primaryButton: {
    backgroundColor: "#F97B18",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#D05D02",
    },
  },
  successBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: "#e8f5e9",
    color: "#1b5e20",
    fontWeight: 700,
  },
}));

const initialState = {
  cpf: "",
  loading: false,
  error: null,
  found: false,
};

function hasCustomerResult(data) {
  if (data?.found === true || data?.exists === true) return true;
  if (data?.customer || data?.result) return true;
  if (Array.isArray(data?.results)) return data.results.length > 0;
  if (Array.isArray(data?.data)) return data.data.length > 0;
  if (data?.data && typeof data.data === "object") return hasCustomerResult(data.data) || Object.keys(data.data).length > 0;
  if (Array.isArray(data)) return data.length > 0;
  return false;
}

function SearchCPFComponent({ onSuccess }) {
  const classes = useStyles();
  const [state, setState] = useState(initialState);

  const handleValidate = async () => {
    const cpfNumbers = clearNumber(state.cpf);

    if (cpfNumbers.length !== 11 || !validadeCpf(cpfNumbers)) {
      setState((prev) => ({
        ...prev,
        error: "Informe um CPF válido",
        found: false,
      }));
      return;
    }

    try {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        found: false,
      }));

      const data = await searchCustomerByCpf(cpfNumbers);

      if (!hasCustomerResult(data)) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "CPF não encontrado na agenda",
          found: false,
        }));
        return;
      }

      const formattedCpf = formatCpf(cpfNumbers);
      setState((prev) => ({
        ...prev,
        cpf: formattedCpf,
        loading: false,
        error: null,
        found: true,
      }));
      onSuccess(formattedCpf);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "CPF não encontrado na agenda",
        found: false,
      }));
    }
  };

  const handleRetry = () => {
    setState(initialState);
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>
        CPF do cliente
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph className="!mb-1">
        Para conseguirmos analisar e corrigir o problema, precisamos que você nos informe o <strong>CPF do cliente que está solicitando ajuda</strong>.
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Lembrando que o CPF do cliente precisa estar <strong>cadastrado na Agenda</strong> para conseguirmos localizar as informações da assinatura.
      </Typography>

      {state.found ? (
        <Box className={classes.successBox}>
          <CheckCircleIcon />
          <span>CPF ENCONTRADO</span>
        </Box>
      ) : (
        <>
          <InputMask
            mask="999.999.999-99"
            value={state.cpf}
            onChange={(event) =>
              setState((prev) => ({
                ...prev,
                cpf: event.target.value,
                error: null,
              }))
            }
            disabled={state.loading}
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                fullWidth
                label="CPF"
                variant="outlined"
                error={!!state.error}
                helperText={state.error}
              />
            )}
          </InputMask>

          {state.error === "CPF não encontrado na agenda" && (
            <Alert severity="warning" style={{ marginTop: 16 }}>
              CPF não encontrado na agenda
            </Alert>
          )}

          <Box className={classes.actions}>
            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={handleValidate}
              disabled={state.loading}
              startIcon={state.loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {state.loading ? "Validando..." : "Validar"}
            </Button>
            {state.error === "CPF não encontrado na agenda" && (
              <Button onClick={handleRetry} disabled={state.loading}>
                Tentar outro CPF
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

export default SearchCPFComponent;
